import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight, RotateCcw, Search, Car, Cpu } from "lucide-react";
import { API } from "@/lib/site";

const inputCls = "h-11 w-full border border-line bg-white px-3 text-sm text-ink outline-none focus:border-regal-amber transition-colors";
const labelCls = "mb-1.5 block font-mono text-[10px] uppercase tracking-[0.18em] text-neutral-500";

const TOOL_BADGE = {
  "New Genius": "bg-regal-amber/10 text-regal-amber border border-regal-amber/30",
  "New Trasdata": "bg-sky-50 text-sky-700",
  "My Genius": "bg-emerald-50 text-emerald-700",
};
const CONN_BADGE = {
  OBD: "bg-regal-amber/10 text-regal-amber",
  BENCH: "bg-sky-50 text-sky-700",
  BOOT: "bg-emerald-50 text-emerald-700",
};

function FilterSelect({ label, value, onChange, options, testId }) {
  return (
    <div>
      <label className={labelCls} htmlFor={testId}>{label}</label>
      <select id={testId} data-testid={testId} value={value} onChange={(e) => onChange(e.target.value)} className={inputCls}>
        <option value="">All</option>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}

function useDimsportQuery(apiBase, filters, search) {
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [options, setOptions] = useState(null);
  const [meta, setMeta] = useState({ total: 0 });
  const PER_PAGE = 15;

  const params = useMemo(() => {
    const p = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => v && p.set(k, v));
    if (search.trim()) p.set("search", search.trim());
    return p;
  }, [filters, search]);

  useEffect(() => {
    axios.get(`${API}/${apiBase}/filters?${params}`)
      .then((r) => { setOptions(r.data.options); setMeta({ total: r.data.total || 0 }); })
      .catch(() => {});
  }, [apiBase, params]);

  useEffect(() => {
    setLoading(true);
    setError(false);
    const p = new URLSearchParams(params);
    p.set("page", page);
    p.set("per_page", PER_PAGE);
    axios.get(`${API}/${apiBase}?${p}`)
      .then((r) => { setRows(r.data.rows); setCount(r.data.count); })
      .catch(() => { setRows([]); setCount(0); setError(true); })
      .finally(() => setLoading(false));
  }, [apiBase, params, page]);

  return { rows, count, page, setPage, loading, error, options, meta, pages: Math.max(Math.ceil(count / PER_PAGE), 1) };
}

function Pager({ page, pages, setPage, testId }) {
  if (pages <= 1) return null;
  return (
    <div className="mt-6 flex items-center justify-between" data-testid={`${testId}-pagination`}>
      <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid={`${testId}-prev`} className="border border-linebright px-5 py-2.5 font-display text-xs font-bold uppercase tracking-[0.14em] text-ink hover:border-regal-amber hover:text-regal-amber disabled:opacity-30 transition-colors">Previous</button>
      <p className="font-mono text-[11px] uppercase tracking-[0.16em] text-neutral-500">Page {page} of {pages.toLocaleString()}</p>
      <button disabled={page >= pages} onClick={() => setPage((p) => p + 1)} data-testid={`${testId}-next`} className="border border-linebright px-5 py-2.5 font-display text-xs font-bold uppercase tracking-[0.14em] text-ink hover:border-regal-amber hover:text-regal-amber disabled:opacity-30 transition-colors">Next</button>
    </div>
  );
}

export function DimsportCompatibility() {
  const [tab, setTab] = useState("vehicle");
  const [vf, setVf] = useState({});
  const [ef, setEf] = useState({});
  const [vSearch, setVSearch] = useState("");
  const [eSearch, setESearch] = useState("");
  const v = useDimsportQuery("ds-vehicles", vf, vSearch);
  const e = useDimsportQuery("ds-ecus", ef, eSearch);

  const setV = (k, val) => { v.setPage(1); setVf((f) => ({ ...f, [k]: val })); };
  const setE = (k, val) => { e.setPage(1); setEf((f) => ({ ...f, [k]: val })); };
  const active = tab === "vehicle" ? v : e;

  return (
    <div data-testid="ds-compat">
      <p className="overline-label mb-4">Dimsport Vehicle Compatibility</p>
      <h2 className="max-w-3xl font-display text-2xl sm:text-3xl lg:text-4xl font-bold uppercase tracking-tight text-ink leading-[1.08]">
        New Genius Vehicle &amp; ECU Compatibility
      </h2>
      <p className="mt-4 max-w-2xl text-sm sm:text-base text-neutral-400 leading-relaxed">
        Search the official Dimsport applications database — Car, Bike, Truck, Tractor and Marine — to check whether your vehicle or ECU is supported by Dimsport New Genius and which connection or programming method is available. Data comes from the official Dimsport applications list and is refreshed regularly.
      </p>

      {/* TABS */}
      <div className="mt-8 flex gap-px border border-line bg-line" data-testid="ds-compat-tabs">
        {[["vehicle", Car, "Search by Vehicle"], ["ecu", Cpu, "Search by ECU"]].map(([key, Icon, label]) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            data-testid={`ds-tab-${key}`}
            className={`flex-1 inline-flex items-center justify-center gap-2 px-6 py-4 font-display text-xs sm:text-sm font-bold uppercase tracking-[0.14em] transition-colors ${tab === key ? "bg-ink text-white" : "bg-white text-neutral-500 hover:text-regal-amber"}`}
          >
            <Icon size={16} /> {label}
          </button>
        ))}
      </div>

      {tab === "vehicle" ? (
        <>
          <div className="mt-6 border border-line bg-white p-6 sm:p-8" data-testid="ds-vehicle-filters">
            <div className="relative mb-6">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
              <input value={vSearch} onChange={(ev) => { v.setPage(1); setVSearch(ev.target.value); }} placeholder="Search manufacturer, model or engine…" className={`${inputCls} pl-9`} data-testid="ds-v-search" />
            </div>
            {v.options && (
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                <FilterSelect label="Vehicle Type" value={vf.vehicle_type || ""} onChange={(x) => setV("vehicle_type", x)} options={v.options.vehicle_type} testId="ds-v-filter-type" />
                <FilterSelect label="Manufacturer" value={vf.brand || ""} onChange={(x) => { setVf((f) => ({ ...f, brand: x, model: "" })); v.setPage(1); }} options={v.options.brand} testId="ds-v-filter-brand" />
                <FilterSelect label="Model" value={vf.model || ""} onChange={(x) => setV("model", x)} options={v.options.model} testId="ds-v-filter-model" />
                <FilterSelect label="Dimsport Tool" value={vf.tool || ""} onChange={(x) => setV("tool", x)} options={v.options.tools} testId="ds-v-filter-tool" />
                <FilterSelect label="Connection / Method" value={vf.connection || ""} onChange={(x) => setV("connection", x)} options={v.options.connections} testId="ds-v-filter-connection" />
              </div>
            )}
            <div className="mt-6 flex flex-wrap items-center justify-between gap-4">
              <p className="font-mono text-[11px] uppercase tracking-[0.16em] text-neutral-500" data-testid="ds-v-count">
                {v.loading ? "Searching…" : `${v.count.toLocaleString()} matching application${v.count === 1 ? "" : "s"}`}
              </p>
              {(Object.values(vf).some(Boolean) || vSearch.trim()) && (
                <button onClick={() => { setVf({}); setVSearch(""); v.setPage(1); }} data-testid="ds-v-reset" className="inline-flex items-center gap-2 border border-linebright px-5 py-2.5 font-display text-xs font-bold uppercase tracking-[0.14em] text-ink hover:border-regal-amber hover:text-regal-amber transition-colors">
                  <RotateCcw size={13} /> Reset
                </button>
              )}
            </div>
          </div>

          <div className="mt-6 overflow-x-auto border border-line" data-testid="ds-v-results">
            <table className="w-full min-w-[860px] text-left text-sm">
              <thead>
                <tr className="border-b border-line bg-white">
                  {["Vehicle", "Type / Version", "Power", "Dimsport Tools", "Connection / Method"].map((h) => (
                    <th key={h} className="px-4 py-3 font-mono text-[10px] uppercase tracking-[0.18em] text-neutral-500">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {v.error && <tr><td colSpan={5} className="px-4 py-14 text-center text-sm text-neutral-400" data-testid="ds-v-error">The compatibility database couldn't be loaded just now — please try again, or <Link to="/contact" className="text-regal-amber underline-offset-4 hover:underline">contact us</Link>.</td></tr>}
                {!v.error && !v.loading && v.rows.length === 0 && (
                  <tr><td colSpan={5} className="px-4 py-14 text-center text-sm text-neutral-400" data-testid="ds-v-empty">No applications match those filters — try widening your search, or <Link to="/contact" className="text-regal-amber underline-offset-4 hover:underline">ask us to check coverage</Link>.</td></tr>
                )}
                {v.rows.map((r, i) => (
                  <tr key={`${r.url}-${i}`} className="border-b border-line bg-white last:border-0 hover:bg-surface/60 transition-colors" data-testid="ds-v-row">
                    <td className="px-4 py-3.5">
                      <p className="font-display text-sm font-bold uppercase tracking-wide text-ink">{r.brand} {r.model}</p>
                      <p className="text-xs text-neutral-500">{r.vehicle_type}</p>
                    </td>
                    <td className="px-4 py-3.5 text-xs text-neutral-500 max-w-[280px]">{r.type}</td>
                    <td className="px-4 py-3.5 text-neutral-400">{r.kw ? `${r.kw} kW${r.hp ? ` / ${r.hp} hp` : ""}` : "—"}</td>
                    <td className="px-4 py-3.5">
                      <div className="flex flex-wrap gap-1.5">
                        {(r.tools || []).map((t) => (
                          <span key={t} className={`px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.1em] ${TOOL_BADGE[t] || "bg-surface text-neutral-500"}`}>{t}</span>
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-3.5">
                      <div className="flex flex-wrap gap-1.5">
                        {(r.connections || []).map((c) => (
                          <span key={c} className={`px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.12em] ${CONN_BADGE[c] || "bg-surface text-neutral-500"}`}>{c}</span>
                        ))}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pager page={v.page} pages={v.pages} setPage={v.setPage} testId="ds-v" />
        </>
      ) : (
        <>
          <div className="mt-6 border border-line bg-white p-6 sm:p-8" data-testid="ds-ecu-filters">
            <div className="relative mb-6">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
              <input value={eSearch} onChange={(ev) => { e.setPage(1); setESearch(ev.target.value); }} placeholder="Search ECU manufacturer, model or protocol…" className={`${inputCls} pl-9`} data-testid="ds-e-search" />
            </div>
            {e.options && (
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <FilterSelect label="Vehicle Type" value={ef.vehicle_type || ""} onChange={(x) => setE("vehicle_type", x)} options={e.options.vehicle_type} testId="ds-e-filter-type" />
                <FilterSelect label="ECU Manufacturer" value={ef.ecu_brand || ""} onChange={(x) => { setEf((f) => ({ ...f, ecu_brand: x, ecu_model: "" })); e.setPage(1); }} options={e.options.ecu_brand} testId="ds-e-filter-brand" />
                <FilterSelect label="ECU Model" value={ef.ecu_model || ""} onChange={(x) => setE("ecu_model", x)} options={e.options.ecu_model} testId="ds-e-filter-model" />
                <FilterSelect label="New Genius Protocol" value={ef.protocol || ""} onChange={(x) => setE("protocol", x)} options={e.options.protocol} testId="ds-e-filter-protocol" />
              </div>
            )}
            <p className="mt-6 font-mono text-[11px] uppercase tracking-[0.16em] text-neutral-500" data-testid="ds-e-count">
              {e.loading ? "Searching…" : `${e.count.toLocaleString()} ECU application${e.count === 1 ? "" : "s"}`}
            </p>
          </div>

          <div className="mt-6 overflow-x-auto border border-line" data-testid="ds-e-results">
            <table className="w-full min-w-[860px] text-left text-sm">
              <thead>
                <tr className="border-b border-line bg-white">
                  {["Vehicle Type", "ECU Manufacturer", "ECU Model", "New Genius Protocol", "New Trasdata", "Compatible Manufacturers"].map((h) => (
                    <th key={h} className="px-4 py-3 font-mono text-[10px] uppercase tracking-[0.18em] text-neutral-500">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {e.error && <tr><td colSpan={6} className="px-4 py-14 text-center text-sm text-neutral-400" data-testid="ds-e-error">The compatibility database couldn't be loaded just now — please try again.</td></tr>}
                {!e.error && !e.loading && e.rows.length === 0 && (
                  <tr><td colSpan={6} className="px-4 py-14 text-center text-sm text-neutral-400" data-testid="ds-e-empty">No ECU applications match those filters.</td></tr>
                )}
                {e.rows.map((r, i) => (
                  <tr key={`${r.ecu_brand}-${r.ecu_model}-${r.vehicle_type}-${i}`} className="border-b border-line bg-white last:border-0 hover:bg-surface/60 transition-colors" data-testid="ds-e-row">
                    <td className="px-4 py-3.5 text-neutral-400">{r.vehicle_type}</td>
                    <td className="px-4 py-3.5 font-display text-sm font-bold uppercase tracking-wide text-ink">{r.ecu_brand}</td>
                    <td className="px-4 py-3.5 text-ink">{r.ecu_model}</td>
                    <td className="px-4 py-3.5">
                      {r.protocol ? <span className="bg-regal-amber/10 px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.12em] text-regal-amber">{r.protocol}</span> : <span className="text-neutral-400">—</span>}
                    </td>
                    <td className="px-4 py-3.5 font-mono text-[11px] text-neutral-500">{[r.trasdata_cpu, r.trasdata_plugin].filter(Boolean).join(" · ") || "—"}</td>
                    <td className="px-4 py-3.5 text-xs text-neutral-500 max-w-[240px]">{r.compatible_brands || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pager page={e.page} pages={e.pages} setPage={e.setPage} testId="ds-e" />
        </>
      )}

      <div className="mt-10 flex flex-col items-start justify-between gap-6 border border-line bg-white p-8 sm:flex-row sm:items-center" data-testid="ds-compat-cta">
        <div>
          <h3 className="font-display text-xl sm:text-2xl font-bold uppercase tracking-tight text-ink">Supported? Get equipped.</h3>
          <p className="mt-2 max-w-xl text-sm text-neutral-400">Genuine Dimsport New Genius tools and accessories, supplied with Regal Tuning technical support.</p>
        </div>
        <a href="#dimsport-products" data-testid="ds-compat-cta-link" className="inline-flex shrink-0 items-center gap-2 bg-regal-amber px-8 py-4 font-display text-sm font-bold uppercase tracking-[0.14em] text-white hover:bg-amber-400 transition-colors">
          View Dimsport Products <ArrowRight size={15} />
        </a>
      </div>
    </div>
  );
}
